/**
 * What each equipment appearance key changes about a figure (`OUTFITS`).
 */
import { FASHION } from '../rig/fashionData';
import type { Look } from './types';

/** What each equipment appearance key changes about a figure. */
export const OUTFITS: Record<string, Partial<Look>> = {
  steel: {},
  // School badge ranks of the school uniform (see rig/uniforms.ts uniformTier).
  uniform1: { schoolTier: 1 },
  uniform2: { schoolTier: 2 },
  uniform3: { schoolTier: 3 },
  uniform4: { schoolTier: 4 },
  sheathed: { sheathed: true },
  // กระบี่มารพันกระบี่: a long black blade with a blood-red edge and a gold guard.
  demonblade: {
    blade: '#2a2632',
    bladeS: '#e8303a',
    guard: '#e8b84a',
    hilt: '#1a0a0e',
    tassel: '#c8202a',
    wlen: 26,
    scabbard: '#120a0e',
  },
  // Fashion: น้ำค้างม่วง — long purple flowered robe, white shirt and trousers, black cap with a red
  // flower, gold belt and pendant.
  // Fashion: จิ้งจอกจันทร์คราม — fox spirit in layered royal blue over white, a white fur stole,
  // silver hair with fox ears, gold ornaments and blue tassels.
  fashionAzureFox: {
    // Fashion is worn instead of a school's uniform.
    school: undefined,
    outer: '#2a4aa8',
    outerS: '#1a2e78',
    outerH: '#6a8ae8',
    trim: '#e8c870',
    inner: '#eef2fa',
    innerS: '#b8c4dc',
    sash: '#eef2fa',
    sashS: '#b8c4dc',
    pendant: '#e8c870',
    tassel: '#3a8ae0',
    boots: '#dce0ec',
    bootsS: '#9aa2b8',
    motif: '#8aa8f0',
    coat: { c: '#3a5ac0', s: '#24408e', edge: '#e8c870', len: 16 },
    cape: undefined,
    pauldron: undefined,
    hat: undefined,
    shortRobe: false,
    hair: '#e4e8f2',
    hairH: '#ffffff',
    hairStyle: 'loose',
    headwear: 'gem',
    crown: '#e8c870',
    crownS: '#3a8ae0',
    accent: '#3a8ae0',
    foxEars: { c: '#e4e8f2', s: '#f2c8d0' },
    stole: { c: '#ffffff', s: '#cdd6e6' },
  },
  // Fashion: ชุดจอมมารกระบี่ (เถาฉางเยี่ย) — a ragged black robe with a red hem, long wild black
  // hair, and a long heavy black sword.
  fashionDugu: {
    // Fashion is worn instead of a school's uniform.
    school: undefined,
    outer: '#1c1c22',
    outerS: '#0c0c10',
    outerH: '#3a3a46',
    trim: '#8a1a1a',
    inner: '#2a2a30',
    innerS: '#16161a',
    sash: '#141418',
    sashS: '#0a0a0c',
    pendant: '#8a1a1a',
    tassel: '#8a1a1a',
    boots: '#101012',
    bootsS: '#24242a',
    motif: undefined,
    coat: { c: '#141418', s: '#08080a', edge: '#5a1010', len: 18 },
    cape: undefined,
    pauldron: undefined,
    hat: undefined,
    shortRobe: false,
    hair: '#0e0e12',
    hairH: '#2e2e3a',
    hairStyle: 'loose',
    headwear: 'none',
    ribbon: undefined,
    weapon: 'sword',
    wlen: 28,
    blade: '#3a3a44',
    bladeS: '#141418',
    guard: '#2a2a30',
    hilt: '#1a1a1e',
    scabbard: '#141418',
  },
  fashionPurpleDew: {
    // Fashion is worn instead of a school's uniform.
    school: undefined,
    outer: '#5a2a9a',
    outerS: '#3e1a70',
    outerH: '#8a5ad0',
    trim: '#24142e',
    inner: '#f2eef2',
    innerS: '#c8c0cc',
    sash: '#d8a840',
    sashS: '#a07a2a',
    pendant: '#e8a030',
    tassel: '#e8a030',
    boots: '#16141a',
    bootsS: '#2e2a34',
    motif: '#9a6ae0',
    coat: undefined,
    cape: undefined,
    pauldron: undefined,
    hat: undefined,
    shortRobe: false,
    headwear: 'guan',
    crown: '#141418',
    crownS: '#c8343a',
    accent: '#141418',
  },
  azure: { blade: '#d8f4ff', bladeS: '#5fb8e8', guard: '#7fd8ff', tassel: '#3a8fd0' },
  crimson: {
    blade: '#ffe0e0',
    bladeS: '#c0304a',
    guard: '#e8b84a',
    tassel: '#b02a35',
    hilt: '#2a1418',
    scabbard: '#3a0e14',
  },
  band: { headwear: 'band', accent: '#c8343a', ribbon: undefined },
  douli: { hat: { c: '#c4a468', s: '#8a6e3e' } },
  jadeCrown: { headwear: 'guan', crown: '#6fe0a8', crownS: '#2f9a68', ribbon: '#6fe0a8' },
  coatNavy: { coat: { c: '#1d2a4f', s: '#131c38', edge: '#d9b46c', len: 9 } },
  coatBlack: { coat: { c: '#2b2f3b', s: '#1b1e27', edge: '#c9ced8', len: 8 } },
  coatCrimson: { coat: { c: '#5a1820', s: '#3a0e14', edge: '#e8b84a', len: 11 } },
  pelt: { pelt: { c: '#a9b0c2', s: '#6e7488' }, coat: undefined },
};

// The fashion outfits (rig/fashion.ts, lane C0-OUTFIT), keys `fashion-<id>` (the skins of the same ids, data/skins.ts):
// each is worn instead of the clothes, the school's uniform and the gear's coats, capes and hats; the wearer keeps
// their face, hair (unless the outfit sets a hairstyle) and weapon.
for (const d of FASHION)
  OUTFITS[`fashion-${d.id}`] = {
    school: undefined,
    fashion: d.id,
    coat: undefined,
    cape: undefined,
    pauldron: undefined,
    hat: undefined,
    stole: undefined,
    pelt: undefined,
    mask: undefined,
    foxEars: undefined,
    motif: undefined,
    pendant: undefined,
    ribbon: undefined,
    headwear: 'none',
  };
